/**
 * useToast hook - Access toast context for showing notifications
 *
 * @example
 * const { showSuccessToast, showErrorToast } = useToast()
 * showSuccessToast('Changes saved!')
 * showErrorToast('Something went wrong')
 */

import * as React from 'react'
import type { ToastVariant } from './Toast'

// =============================================================================
// Types
// =============================================================================

export interface ToastData {
  id: string
  title?: string
  description: string
  variant: ToastVariant
  duration?: number
  /** One button on the toast, e.g. Undo or View. */
  action?: { label: string; onClick: () => void }
}

export interface ToastContextValue {
  toasts: ToastData[]
  addToast: (toast: Omit<ToastData, 'id'>) => string
  removeToast: (id: string) => void
  showSuccessToast: (description: string, title?: string) => string
  showErrorToast: (description: string, title?: string) => string
  showInfoToast: (description: string, title?: string) => string
  showWarningToast: (description: string, title?: string) => string
}

// =============================================================================
// Context
// =============================================================================

export const ToastContext = React.createContext<ToastContextValue | null>(null)

// =============================================================================
// Hook
// =============================================================================

export function useToast(): ToastContextValue {
  const context = React.useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}
